<template>
  <client-page>
    <main class="pv-page">
      <!-- =====================================================
        HERO
      ====================================================== -->
      <section class="pv-hero" aria-labelledby="privacy-title">
        <div class="pv-container pv-hero-grid">
          <div>
            <p class="pv-label">PRIVACY POLICY</p>

            <h1 id="privacy-title">
              개인정보를 투명하게,<br />
              <span>필요한 범위에서만</span> 처리합니다.
            </h1>
          </div>

          <div class="pv-hero-side">
            <p>
              MUZIN은 프로젝트 문의, AI 진단과 상담 연결을 위해
              필요한 개인정보를 처리하며, 처리 목적이 달성되면
              정해진 절차에 따라 안전하게 파기합니다.
            </p>

            <dl>
              <div>
                <dt>시행일</dt>
                <dd>{{ policy.effectiveDate }}</dd>
              </div>

              <div>
                <dt>버전</dt>
                <dd>{{ policy.version }}</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <!-- =====================================================
        SUMMARY
      ====================================================== -->
      <section class="pv-summary" aria-label="개인정보 처리방침 요약">
        <div class="pv-container pv-summary-grid">
          <article
            v-for="item in summaryItems"
            :key="item.number"
          >
            <span>{{ item.number }}</span>

            <div class="pv-summary-icon">
              <v-icon color="#111111" size="27" aria-hidden="true">
                {{ item.icon }}
              </v-icon>
            </div>

            <small>{{ item.english }}</small>
            <h2>{{ item.title }}</h2>
            <p>{{ item.description }}</p>
          </article>
        </div>
      </section>

      <!-- =====================================================
        POLICY CONTENT
      ====================================================== -->
      <section class="pv-content">
        <div class="pv-container pv-content-grid">
          <aside class="pv-toc">
            <p class="pv-label">CONTENTS</p>

            <nav aria-label="개인정보 처리방침 목차">
              <button
                v-for="section in sections"
                :key="section.id"
                type="button"
                :class="{ 'is-active': activeSection === section.id }"
                @click="scrollToSection(section.id)"
              >
                <span>{{ section.number }}</span>
                <strong>{{ section.shortTitle }}</strong>
              </button>
            </nav>

            <div class="pv-contact-card">
              <small>PRIVACY CONTACT</small>
              <strong>{{ policy.officer.name }}</strong>
              <p>{{ policy.officer.position }}</p>

              <a :href="`mailto:${policy.officer.email}`">
                {{ policy.officer.email }}
              </a>

              <a :href="`tel:${policy.officer.phoneLink}`">
                {{ policy.officer.phoneDisplay }}
              </a>
            </div>
          </aside>

          <article class="pv-policy-document">
            <header class="pv-document-intro">
              <p>
                MUZIN(이하 “회사”)은 정보주체의 자유와 권리 보호를 위해
                개인정보 보호 관련 법령을 준수하며, 개인정보 처리에 관한
                절차와 기준을 다음과 같이 안내합니다.
              </p>
            </header>

            <section id="privacy-requested-info" class="pv-policy-section" data-policy-section="privacy-requested-info">
              <header><span>12</span><div><small>REQUESTED INFORMATION</small><h2>자료 요청과 1회 전달</h2></div></header>
              <p>이 항목은 제작 실무 자료실에 공개된 기업·Creator 자료 요청에 적용됩니다. 자료 요청에 대한 동의는 회사소개·견적·상담·뉴스레터 등 마케팅 수신 동의가 아닙니다. 자료 전달 권한은 요청 자료 1회 사용 후 소진되며, 보관 중인 증빙을 다른 발송의 동의로 재사용하지 않습니다.</p>
              <h3>수집항목과 이용목적</h3>
              <p>이메일, 선택한 자료, 요청·인증·전달 시각, 유입 위치와 같은 출처의 페이지 경로, 요청·확인 IP, 개인정보 안내 버전과 동의 여부, 명시적 1회 요청 문구, 인증 성공 여부·시도 횟수·유효기간, 인증번호와 임시 브라우저 토큰의 해시, 요청 증빙 서명, 메일 메시지 식별자와 전달 결과를 처리합니다. 본인 확인, 선택한 자료의 1회 전달, 중복·오발송 방지, 요청 사실 확인에 사용합니다. 이름·회사명·전화번호·브라우저 지문은 자료 요청의 필수 수집항목이 아닙니다. 자료 요청 데이터는 AI 분석으로 보내지 않습니다.</p>
              <h3>보유기간과 파기기준</h3>
              <p>요청 증빙과 해당 요청의 발송 이력은 요청일부터 90일 보관합니다. 인증번호는 해시로만 저장하고, 인증 성공 시 즉시 또는 10분 유효기간 경과 후 정기 정리 때 삭제합니다. 매시간 보유기간을 확인하여 만료된 요청·법적 근거 증빙·해당 요청의 발송 및 중복방지 기록을 삭제합니다. 이 보유기간은 자료의 재발송 권한을 의미하지 않습니다. 삭제·처리정지·동의 철회는 hello@muzin.co로 요청할 수 있습니다.</p>
              <p>별도 회신·상담요청을 한 경우에는 실제 수신 원문, 발신 주소, 수신 시각, 메시지 식별자와 응대 범위를 확인합니다. 자료요청 기록에 연결한 증빙에는 동일한 90일 보유기간을 적용하며, 별도의 마케팅 동의로 간주하지 않습니다.</p>
              <h3>자료별 이용 단계 집계</h3>
              <p>자료 노출·선택·유효한 형식의 이메일 입력 단계는 자료 종류와 일별 건수만 저장합니다. 이메일 입력값·IP·요청ID는 이 집계에 저장하지 않습니다. 중복 집계를 줄이기 위한 페이지별 임의 식별자는 서버 메모리에 최대 30분만 유지하며 쿠키나 로컬 저장소에 저장하지 않고 요청 기록과 연결하지 않습니다. 인증·요청 완료는 서버의 실제 처리 기록으로 집계합니다. 통계는 90일 범위로 제공하며 이용자의 고유 인원이나 개별 행동 이력을 뜻하지 않습니다.</p>
              <h3>발송 처리와 접속 로그</h3>
              <p>인증메일과 요청 자료는 NAVER WORKS 회사메일을 통해 전송합니다. 발송에 필요한 이메일 주소·요청 자료·메시지 정보만 메일 전송에 사용합니다. 웹 서버는 보안·장애 대응용으로 접속 IP, 시각, 요청 경로, 응답 상태, 브라우저 접속정보를 기록합니다. 접속 로그는 일 단위로 회전하며 현재 로그와 최근 14개 회전 파일을 관리합니다. 이메일·인증번호를 URL에 넣거나 요청 본문 전체를 접속 로그에 기록하지 않습니다.</p>
              <p>개인정보 수집·이용 동의를 거부할 수 있으며 이 경우 이메일 인증과 자료 전달을 이용할 수 없습니다. 자료요청 동의를 거부하거나 철회한 것을 영업·뉴스레터 동의로 해석하지 않습니다.</p>
            </section>

            <section class="pv-policy-section" id="privacy-startup">
              <header><span>13</span><div><small>STARTUP DIAGNOSIS &amp; REVIEW</small><h2>창업기업 진단과 전문가 리뷰</h2></div></header>
              <p>Startup 진단은 창업단계, 업력, 제품·서비스, 매출·투자 여부, 지원사업 경험, 필요자금, 예정 일정과 어려운 문제를 분석해 실행 방향을 정리합니다. 동의 후 AI 분석을 진행하며 연락처는 무료 진단의 필수 입력항목이 아닙니다. 개인정보와 영업비밀은 입력하지 마세요. 진단 데이터는 1년 보관하며 결과를 브라우저 세션과 파일로 저장할 수 있습니다.</p>
              <p>전문가 리뷰의 온라인 결제가 개시되면 이름, 이메일, 주문번호, 금액, 승인 결과와 동의 기록을 주문·결제 확인에 이용합니다. 결제된 고객이 제출한 사업상황·자료 링크·희망 일정은 리뷰와 일정 조율에만 이용합니다. 카드정보를 MUZIN 서버에 저장하지 않습니다. 결제창과 결제 승인은 토스페이먼츠 연동을 준비 중이며, 실제 판매 개시 전 결제 위탁과 보유기간을 최종 안내합니다.</p>
              <p>Startup 페이지 방문·진단 시작 등은 이벤트 종류와 일별 횟수만 90일 집계합니다. 이 집계에는 입력 내용, 이메일, IP, 주문번호나 고유 이용자 식별자를 저장하지 않습니다. 진단 완료·결제 승인은 서버 처리 결과로 집계하며 테스트와 실결제는 구분합니다. 광고 동의와 ENGAGED 상태로 자동 변경하지 않습니다.</p>
            </section>
            <section class="pv-policy-section" id="privacy-mizi-account">
              <header><span>14</span><div><small>MIZI ACCOUNT / 2026-10-01</small><h2>MIZI 계정과 내 사업정보</h2></div></header>
              <p>MIZI 무료 계정은 이메일 인증으로 생성합니다. 이메일, 계정 식별자, 필수 동의 버전과 시각, 사업정보, 생성한 결과, 과제 상태, 분석한 공고, 기능 사용량과 구독 상태를 계정 운영과 반복 사용에 이용합니다. 사업정보에는 회사·프로젝트, 단계·업력·지역·업종, 제품·고객·문제·해결방식·BM, 매출·투자·지원사업 경험, 목표·기한·실행 이벤트가 포함됩니다.</p>
              <p>AI 생성 시 사용자가 입력한 사업정보, 이전 계획, 미완료 과제와 제공한 공고 본문 또는 파일을 OpenAI API에 전달합니다. 생성 요청의 API 저장은 비활성화하며, 제공자가 처리하는 범위는 아래 AI 처리 안내를 함께 확인하세요. 필요하지 않은 개인정보·비밀정보는 입력하지 마세요. 이메일과 인증번호는 AI 입력에 포함하지 않습니다.</p>
              <p>인증번호는 해시로 저장하고 10분 후 만료됩니다. 계정 접속 쿠키는 HttpOnly·Secure·SameSite로 설정하고 최대 30일 유효합니다. 서버에는 접속 토큰의 해시만 저장합니다. 만료된 인증과 세션은 매시간 정리하며, 로그아웃하면 해당 접속 세션을 삭제합니다.</p>
              <p>사업정보와 결과는 계정 사용을 이어가기 위해 계정 운영기간 동안 보관합니다. 수정은 내 사업정보·Library에서 가능하며 계정 삭제와 동의 철회는 hello@muzin.co로 요청할 수 있습니다. 계정 생성·사업정보 완료·첫 결과·재사용·사용량·구독 이벤트는 제품 운영과 전환 집계에 사용합니다. 테스트 계정은 실제 회원·매출 집계에서 제외합니다.</p>
              <p>계정 인증과 자료 생성 동의는 광고 수신 동의가 아닙니다. 정기결제는 PG 연동 전까지 비활성이며 카드 등록과 청구를 진행하지 않습니다. 기존 Startup 진단·주문 기록은 보존하며 Startup Review 전문가 리뷰는 신규 공개 판매하지 않습니다.</p>
            </section>
            <!-- 01 -->
            <section
              id="privacy-purpose"
              class="pv-policy-section"
              data-policy-section="privacy-purpose"
            >
              <header>
                <span>01</span>
                <div>
                  <small>PROCESSING PURPOSE</small>
                  <h2>개인정보의 처리 목적</h2>
                </div>
              </header>

              <p>
                회사는 다음 목적을 위해 개인정보를 처리합니다.
                처리한 개인정보는 아래 목적 외의 용도로 이용하지 않으며,
                목적이 변경되는 경우 필요한 절차를 거칩니다.
              </p>

              <div class="pv-purpose-grid">
                <article
                  v-for="item in purposes"
                  :key="item.title"
                >
                  <h3>{{ item.title }}</h3>
                  <p>{{ item.description }}</p>
                </article>
              </div>
            </section>

            <!-- 02 -->
            <section
              id="privacy-items"
              class="pv-policy-section"
              data-policy-section="privacy-items"
            >
              <header>
                <span>02</span>
                <div>
                  <small>COLLECTED DATA</small>
                  <h2>처리하는 개인정보의 항목</h2>
                </div>
              </header>

              <div class="pv-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>구분</th>
                      <th>필수 항목</th>
                      <th>선택 항목</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr
                      v-for="row in collectedItems"
                      :key="row.type"
                    >
                      <th>{{ row.type }}</th>
                      <td>{{ row.required }}</td>
                      <td>{{ row.optional }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <!-- 03 -->
            <section
              id="privacy-retention"
              class="pv-policy-section"
              data-policy-section="privacy-retention"
            >
              <header>
                <span>03</span>
                <div>
                  <small>RETENTION PERIOD</small>
                  <h2>개인정보의 처리 및 보유 기간</h2>
                </div>
              </header>

              <p>
                개인정보는 원칙적으로 처리 목적을 달성한 뒤 지체 없이 파기합니다.
                다만 상담 이력 확인, 분쟁 대응 또는 관계 법령 준수를 위해
                아래 기간 동안 보관할 수 있습니다.
              </p>

              <div class="pv-retention-list">
                <article
                  v-for="item in retentionItems"
                  :key="item.title"
                >
                  <div>
                    <small>{{ item.english }}</small>
                    <h3>{{ item.title }}</h3>
                  </div>

                  <strong>{{ item.period }}</strong>
                  <p>{{ item.note }}</p>
                </article>
              </div>

              <div class="pv-notice">
                <v-icon color="#111111" size="22" aria-hidden="true">
                  mdi-information-outline
                </v-icon>

                <p>
                  실제 운영 정책에 따라 보유기간을 변경할 경우,
                  변경된 기간을 이 처리방침에 반영합니다.
                </p>
              </div>
            </section>

            <!-- 04 -->
            <section
              id="privacy-provision"
              class="pv-policy-section"
              data-policy-section="privacy-provision"
            >
              <header>
                <span>04</span>
                <div>
                  <small>THIRD PARTY</small>
                  <h2>개인정보의 제3자 제공</h2>
                </div>
              </header>

              <p>
                회사는 원칙적으로 정보주체의 개인정보를 처리 목적 범위 내에서
                이용하며, 정보주체의 동의 또는 법령상 근거 없이 제3자에게
                제공하지 않습니다.
              </p>

              <div class="pv-empty-state">
                <span>현재 정기적인 제3자 제공 없음</span>
                <p>
                  향후 제3자 제공이 발생하는 경우 제공받는 자, 제공 목적,
                  제공 항목과 보유기간을 사전에 안내합니다.
                </p>
              </div>
            </section>

            <!-- 05 -->
            <section
              id="privacy-outsourcing"
              class="pv-policy-section"
              data-policy-section="privacy-outsourcing"
            >
              <header>
                <span>05</span>
                <div>
                  <small>PROCESSING PARTNERS</small>
                  <h2>개인정보 처리업무의 위탁</h2>
                </div>
              </header>

              <p>
                회사는 서비스 운영을 위해 개인정보 처리업무의 일부를
                전문 서비스에 맡길 수 있으며, 위탁 시 개인정보가 안전하게
                관리되도록 필요한 사항을 확인합니다.
              </p>

              <div class="pv-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>수탁자 또는 서비스</th>
                      <th>위탁 업무</th>
                      <th>처리 범위</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr
                      v-for="row in processors"
                      :key="row.provider"
                    >
                      <th>{{ row.provider }}</th>
                      <td>{{ row.purpose }}</td>
                      <td>{{ row.scope }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <!-- 06 -->
            <section
              id="privacy-overseas"
              class="pv-policy-section"
              data-policy-section="privacy-overseas"
            >
              <header>
                <span>06</span>
                <div>
                  <small>OVERSEAS TRANSFER</small>
                  <h2>개인정보의 국외 이전</h2>
                </div>
              </header>

              <p>
                AI 진단 과정에서 입력한 프로젝트 정보는 생성형 AI 분석을 위해
                해외에 위치한 서비스 인프라에서 처리될 수 있습니다.
                연락처와 같이 AI 분석에 불필요한 정보는 분리하거나 최소화합니다.
              </p>

              <div class="pv-overseas-card">
                <dl>
                  <div>
                    <dt>이전받는 서비스</dt>
                    <dd>{{ overseasTransfer.recipient }}</dd>
                  </div>

                  <div>
                    <dt>이전 목적</dt>
                    <dd>{{ overseasTransfer.purpose }}</dd>
                  </div>

                  <div>
                    <dt>이전 항목</dt>
                    <dd>{{ overseasTransfer.items }}</dd>
                  </div>

                  <div>
                    <dt>이전 시점·방법</dt>
                    <dd>{{ overseasTransfer.method }}</dd>
                  </div>

                  <div>
                    <dt>보유·이용 기간</dt>
                    <dd>{{ overseasTransfer.retention }}</dd>
                  </div>

                  <div>
                    <dt>이전 거부 방법</dt>
                    <dd>{{ overseasTransfer.refusal }}</dd>
                  </div>
                </dl>
              </div>
            </section>

            <!-- 07 -->
            <section
              id="privacy-destruction"
              class="pv-policy-section"
              data-policy-section="privacy-destruction"
            >
              <header>
                <span>07</span>
                <div>
                  <small>DESTRUCTION</small>
                  <h2>개인정보의 파기 절차 및 방법</h2>
                </div>
              </header>

              <div class="pv-destruction-grid">
                <article>
                  <span>01</span>
                  <h3>파기 절차</h3>
                  <p>
                    보유기간 경과 또는 처리 목적 달성 시 파기 대상 정보를 확인하고,
                    내부 절차에 따라 파기합니다.
                  </p>
                </article>

                <article>
                  <span>02</span>
                  <h3>전자적 파일</h3>
                  <p>
                    복구 또는 재생되지 않도록 안전한 방식으로 삭제합니다.
                  </p>
                </article>

                <article>
                  <span>03</span>
                  <h3>출력물·서면</h3>
                  <p>
                    분쇄 또는 소각 등 내용을 알아볼 수 없는 방식으로 파기합니다.
                  </p>
                </article>
              </div>
            </section>

            <!-- 08 -->
            <section
              id="privacy-rights"
              class="pv-policy-section"
              data-policy-section="privacy-rights"
            >
              <header>
                <span>08</span>
                <div>
                  <small>YOUR RIGHTS</small>
                  <h2>정보주체의 권리와 행사 방법</h2>
                </div>
              </header>

              <p>
                정보주체는 자신의 개인정보에 대해 열람, 정정·삭제,
                처리정지와 동의 철회를 요청할 수 있습니다.
              </p>

              <ul class="pv-rights-list">
                <li
                  v-for="right in rights"
                  :key="right"
                >
                  {{ right }}
                </li>
              </ul>

              <a
                class="pv-action-link"
                :href="`mailto:${policy.officer.email}?subject=${encodedRightsSubject}`"
              >
                <span>개인정보 권리 행사 요청</span>
                <v-icon color="#111111" size="19" aria-hidden="true">
                  mdi-arrow-top-right
                </v-icon>
              </a>
            </section>

            <!-- 09 -->
            <section
              id="privacy-security"
              class="pv-policy-section"
              data-policy-section="privacy-security"
            >
              <header>
                <span>09</span>
                <div>
                  <small>SECURITY</small>
                  <h2>개인정보의 안전성 확보 조치</h2>
                </div>
              </header>

              <div class="pv-security-grid">
                <article
                  v-for="item in securityItems"
                  :key="item.title"
                >
                  <div class="pv-security-icon">
                    <v-icon color="#111111" size="25" aria-hidden="true">
                      {{ item.icon }}
                    </v-icon>
                  </div>

                  <h3>{{ item.title }}</h3>
                  <p>{{ item.description }}</p>
                </article>
              </div>
            </section>

            <!-- 10 -->
            <section
              id="privacy-officer"
              class="pv-policy-section"
              data-policy-section="privacy-officer"
            >
              <header>
                <span>10</span>
                <div>
                  <small>PRIVACY OFFICER</small>
                  <h2>개인정보 보호책임자 및 문의처</h2>
                </div>
              </header>

              <div class="pv-officer-card">
                <div class="pv-officer-symbol" aria-hidden="true">
                  <i></i>
                  <i></i>
                </div>

                <div>
                  <small>개인정보 보호책임자</small>
                  <h3>{{ policy.officer.name }}</h3>
                  <p>{{ policy.officer.position }}</p>
                </div>

                <dl>
                  <div>
                    <dt>이메일</dt>
                    <dd>
                      <a :href="`mailto:${policy.officer.email}`">
                        {{ policy.officer.email }}
                      </a>
                    </dd>
                  </div>

                  <div>
                    <dt>전화</dt>
                    <dd>
                      <a :href="`tel:${policy.officer.phoneLink}`">
                        {{ policy.officer.phoneDisplay }}
                      </a>
                    </dd>
                  </div>
                </dl>
              </div>
            </section>

            <!-- 11 -->
            <section
              id="privacy-changes"
              class="pv-policy-section"
              data-policy-section="privacy-changes"
            >
              <header>
                <span>11</span>
                <div>
                  <small>POLICY CHANGES</small>
                  <h2>개인정보 처리방침의 변경</h2>
                </div>
              </header>

              <p>
                본 처리방침의 내용이 변경되는 경우 시행일 전에
                홈페이지를 통해 변경 사항을 안내합니다.
              </p>

              <div class="pv-version-row">
                <span>{{ policy.version }}</span>
                <strong>{{ policy.effectiveDate }} 시행</strong>
                <p>자료요청의 수집항목·목적·보유기간·파기 및 비식별 집계 안내 추가</p>
              </div>
            </section>
          </article>
        </div>
      </section>
    </main>
  </client-page>
</template>

<script>
import ClientPage from "@/pages/client/templates/ClientPage.vue";

export default {
  name: "PrivacyPolicyPage",

  components: {
    ClientPage
  },

  data() {
    return {
      activeSection: "privacy-purpose",
      observer: null,

      /*
       * 공개 전 실제 운영정보를 최종 확인하세요.
       * 특히 보유기간, 처리 수탁자, 국외 이전 항목은
       * 서버·호스팅·메일·OpenAI 계약 설정과 일치해야 합니다.
       */
      policy: {
        effectiveDate: "2026.09.22",
        version: "v1.1 · 자료요청",

        officer: {
          name: "차보람",
          position: "MUZIN 대표",
          email: "hello@muzin.co",
          phoneDisplay: "010-4736-1267",
          phoneLink: "01047361267"
        }
      },

      sections: [
        {id:"privacy-requested-info",number:"12",shortTitle:"자료 요청"},
        {
          id: "privacy-purpose",
          number: "01",
          shortTitle: "처리 목적"
        },
        {
          id: "privacy-items",
          number: "02",
          shortTitle: "처리 항목"
        },
        {
          id: "privacy-retention",
          number: "03",
          shortTitle: "보유 기간"
        },
        {
          id: "privacy-provision",
          number: "04",
          shortTitle: "제3자 제공"
        },
        {
          id: "privacy-outsourcing",
          number: "05",
          shortTitle: "처리 위탁"
        },
        {
          id: "privacy-overseas",
          number: "06",
          shortTitle: "국외 이전"
        },
        {
          id: "privacy-destruction",
          number: "07",
          shortTitle: "파기"
        },
        {
          id: "privacy-rights",
          number: "08",
          shortTitle: "정보주체 권리"
        },
        {
          id: "privacy-security",
          number: "09",
          shortTitle: "안전성 조치"
        },
        {
          id: "privacy-officer",
          number: "10",
          shortTitle: "책임자"
        },
        {
          id: "privacy-changes",
          number: "11",
          shortTitle: "방침 변경"
        }
      ],

      summaryItems: [
        {
          number: "01",
          icon: "mdi-account-outline",
          english: "MINIMUM DATA",
          title: "필요한 정보만",
          description:
            "프로젝트 상담과 AI 진단에 필요한 범위에서 개인정보를 처리합니다."
        },
        {
          number: "02",
          icon: "mdi-timer-sand",
          english: "LIMITED RETENTION",
          title: "정해진 기간만",
          description:
            "처리 목적이 끝나거나 보유기간이 지나면 안전하게 파기합니다."
        },
        {
          number: "03",
          icon: "mdi-shield-lock-outline",
          english: "SECURE PROCESS",
          title: "안전하게 관리",
          description:
            "접근 제한, 비밀정보 분리와 보안 설정을 통해 보호합니다."
        },
        {
          number: "04",
          icon: "mdi-account-check-outline",
          english: "YOUR CONTROL",
          title: "권리 행사 보장",
          description:
            "열람, 정정, 삭제, 처리정지와 동의 철회를 요청할 수 있습니다."
        }
      ],

      purposes: [
        {
          title: "프로젝트 문의",
          description:
            "기업·브랜드의 프로젝트 문의 접수, 상담 내용 확인과 후속 연락"
        },
        {
          title: "AI 프로젝트 진단",
          description:
            "기업 조건과 프로젝트 정보를 구조화하고 실행 방향을 제안"
        },
        {
          title: "지원사업 검토",
          description:
            "입력 조건을 기준으로 공식 지원사업을 탐색하고 결과 보고서 생성"
        },
        {
          title: "고객 관계 관리",
          description:
            "상담 상태, 담당자, 후속 연락 일정과 프로젝트 진행 이력 관리"
        },
        {
          title: "선택 정보 제공",
          description:
            "별도 동의한 경우 지원사업·콘텐츠 제작 관련 정보 제공"
        }
      ],

      collectedItems: [
        {
          type: "프로젝트 문의",
          required:
            "기업·브랜드명, 담당자명, 이메일, 프로젝트 유형, 프로젝트 상세 내용, 개인정보 동의 여부",
          optional:
            "직급·담당 역할, 전화번호, 예산, 일정, 연락 방식·시간, 마케팅 동의"
        },
        {
          type: "AI 프로젝트 진단",
          required:
            "세션 ID, 대화 내용, 기업·프로젝트 조건, 진단 결과",
          optional:
            "지원사업 보고서, 상담 전환 정보, 사용자가 대화 중 추가 입력한 정보"
        },
        {
          type: "관리자 CRM",
          required:
            "리드 상태, 접수일시, 상담 목적",
          optional:
            "담당자, 후속 연락일, 관리자 메모, 최종 상담 상태"
        }
      ],

      retentionItems: [
        {
          english: "PROJECT INQUIRY",
          title: "프로젝트 문의 및 상담 정보",
          period: "상담 종료 후 1년",
          note:
            "분쟁 대응 또는 후속 문의 확인이 필요한 경우에 한하며, 목적 달성 후 파기합니다."
        },
        {
          english: "AI SESSION",
          title: "AI 진단 세션과 보고서",
          period: "마지막 이용일로부터 1년",
          note:
            "사용자의 삭제 요청 또는 서비스 운영정책 변경 시 더 일찍 파기할 수 있습니다."
        },
        {
          english: "MARKETING",
          title: "선택 정보 수신 동의",
          period: "동의 철회 또는 2년",
          note:
            "동의 철회 시 관련 연락처와 수신 동의 기록을 지체 없이 정리합니다."
        }
      ],

      processors: [
        {
          provider: "이메일 발송 서비스",
          purpose: "신규 문의 알림 및 후속 연락 이메일 발송",
          scope: "담당자 이메일, 문의 알림에 필요한 최소 정보"
        },
        {
          provider: "서버·호스팅 서비스",
          purpose: "웹사이트, AI 세션과 관리자 CRM 운영",
          scope: "서비스 운영 과정에서 저장되는 문의·세션 정보"
        },
        {
          provider: "OpenAI API",
          purpose: "AI 대화, 프로젝트 정보 구조화 및 보고서 문장 생성",
          scope: "AI 분석에 필요한 대화와 프로젝트 정보"
        }
      ],

      overseasTransfer: {
        recipient: "OpenAI API 서비스",
        purpose:
          "AI 대화 응답, 프로젝트 정보 구조화와 지원사업 분석 문장 생성",
        items:
          "세션 ID, AI 대화 내용, 기업·프로젝트 조건과 보고서 생성에 필요한 정보",
        method:
          "사용자가 AI 기능을 이용할 때 암호화된 네트워크를 통한 전송",
        retention:
          "회사의 API 계약 및 데이터 처리 설정에 따른 기간",
        refusal:
          "AI 진단 기능 이용을 중단하고 직접 프로젝트 문의 방식으로 상담 가능"
      },

      rights: [
        "개인정보 열람 요청",
        "개인정보 정정 또는 삭제 요청",
        "개인정보 처리정지 요청",
        "개인정보 수집·이용 동의 철회",
        "권리 행사를 대리인을 통해 요청",
        "요청 처리 결과에 대한 안내 수령"
      ],

      securityItems: [
        {
          icon: "mdi-account-lock-outline",
          title: "접근 권한 관리",
          description:
            "관리자 CRM과 서버 데이터에 대한 접근을 필요한 담당자로 제한합니다."
        },
        {
          icon: "mdi-key-variant",
          title: "비밀정보 분리",
          description:
            "API 키와 관리자 인증정보를 공개 코드와 분리해 환경변수로 관리합니다."
        },
        {
          icon: "mdi-shield-check-outline",
          title: "전송·보관 보호",
          description:
            "서비스 운영환경에서 개인정보의 유출과 무단 접근을 줄이기 위한 보안 설정을 적용합니다."
        },
        {
          icon: "mdi-delete-clock-outline",
          title: "보유기간 관리",
          description:
            "처리 목적과 보유기간을 기준으로 삭제 대상을 확인하고 파기합니다."
        }
      ]
    };
  },

  computed: {
    encodedRightsSubject() {
      return encodeURIComponent(
        "[MUZIN] 개인정보 권리 행사 요청"
      );
    }
  },

  mounted() {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto"
    });

    this.setupSectionObserver();
  },

  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },

  methods: {
    setupSectionObserver() {
      const targets = Array.from(
        document.querySelectorAll(
          "[data-policy-section]"
        )
      );

      if (
        !targets.length ||
        typeof IntersectionObserver ===
          "undefined"
      ) {
        return;
      }

      this.observer =
        new IntersectionObserver(
          (entries) => {
            const visibleEntries =
              entries
                .filter(
                  (entry) =>
                    entry.isIntersecting
                )
                .sort(
                  (a, b) =>
                    b.intersectionRatio -
                    a.intersectionRatio
                );

            if (visibleEntries[0]) {
              this.activeSection =
                visibleEntries[0].target.id;
            }
          },
          {
            rootMargin:
              "-18% 0px -62% 0px",
            threshold: [
              0,
              0.15,
              0.35,
              0.6
            ]
          }
        );

      targets.forEach((target) => {
        this.observer.observe(target);
      });
    },

    scrollToSection(sectionId) {
      const target =
        document.getElementById(sectionId);

      if (!target) {
        return;
      }

      target.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@500;600;700;800;900&family=Montserrat:wght@700;800;900&display=swap");
@import url("https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.css");

.pv-page {
  --black: #111111;
  --white: #ffffff;
  --paper: #f7f7f4;
  --lime: #c7ff19;
  --lime-dark: #789b00;
  --line: #ddddda;
  --muted: #686865;

  min-height: 100vh;
  color: var(--black);
  background: var(--white);
  font-family: "Pretendard", sans-serif;
}

.pv-page *,
.pv-page *::before,
.pv-page *::after {
  box-sizing: border-box;
}

.pv-page button {
  font-family: inherit;
}

.pv-container {
  width: min(1360px, calc(100% - 72px));
  margin: 0 auto;
}

.pv-label {
  margin: 0;
  color: var(--lime-dark);
  font-family: "Inter", sans-serif;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.14em;
}

/* HERO */
.pv-hero {
  padding: 108px 0 102px;
  border-bottom: 1px solid var(--line);
  background:
    radial-gradient(
      circle at 84% 34%,
      rgba(199, 255, 25, 0.18),
      transparent 23%
    ),
    #ffffff;
}

.pv-hero-grid {
  display: grid;
  grid-template-columns:
    minmax(0, 0.65fr)
    minmax(360px, 0.35fr);
  gap: 82px;
  align-items: end;
}

.pv-hero h1 {
  margin: 22px 0 0;
  font-size: clamp(49px, 5.3vw, 83px);
  line-height: 1.08;
  letter-spacing: -0.058em;
  word-break: keep-all;
}

.pv-hero h1 span {
  text-decoration: underline;
  text-decoration-color: var(--lime);
  text-decoration-thickness: 8px;
  text-underline-offset: 10px;
}

.pv-hero-side > p {
  margin: 0;
  color: var(--muted);
  font-size: 17px;
  line-height: 1.76;
  word-break: keep-all;
}

.pv-hero-side dl {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin: 29px 0 0;
}

.pv-hero-side dl > div {
  padding: 15px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.75);
}

.pv-hero-side dt {
  color: #858581;
  font-size: 10px;
}

.pv-hero-side dd {
  margin: 7px 0 0;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 850;
}

/* SUMMARY */
.pv-summary {
  background: var(--paper);
}

.pv-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border-left: 1px solid var(--line);
}

.pv-summary-grid article {
  min-height: 305px;
  padding: 28px;
  border-right: 1px solid var(--line);
}

.pv-summary-grid article > span {
  color: var(--lime-dark);
  font-family: "Inter", sans-serif;
  font-size: 11px;
  font-weight: 900;
}

.pv-summary-icon {
  display: grid;
  width: 53px;
  height: 53px;
  margin-top: 45px;
  border-radius: 50%;
  background: var(--lime);
  place-items: center;
}

.pv-summary-grid small {
  display: block;
  margin-top: 24px;
  color: #858581;
  font-family: "Inter", sans-serif;
  font-size: 9px;
  font-weight: 850;
  letter-spacing: 0.08em;
}

.pv-summary-grid h2 {
  margin: 9px 0 0;
  font-size: 21px;
  letter-spacing: -0.03em;
}

.pv-summary-grid p {
  margin: 11px 0 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.62;
  word-break: keep-all;
}

/* CONTENT */
.pv-content {
  padding: 112px 0 120px;
}

.pv-content-grid {
  display: grid;
  grid-template-columns:
    minmax(240px, 0.24fr)
    minmax(0, 0.76fr);
  gap: 62px;
  align-items: start;
}

.pv-toc {
  position: sticky;
  top: 116px;
}

.pv-toc nav {
  display: grid;
  margin-top: 21px;
  border-top: 1px solid var(--line);
}

.pv-toc nav button {
  display: grid;
  grid-template-columns: 31px minmax(0, 1fr);
  gap: 11px;
  min-height: 48px;
  padding: 0 10px;
  border: 0;
  border-bottom: 1px solid var(--line);
  text-align: left;
  background: #ffffff;
  cursor: pointer;
}

.pv-toc nav button:hover,
.pv-toc nav button.is-active {
  background: #f1ffd2;
}

.pv-toc nav span {
  color: var(--lime-dark);
  font-family: "Inter", sans-serif;
  font-size: 9px;
  font-weight: 900;
}

.pv-toc nav strong {
  font-size: 12px;
}

.pv-contact-card {
  display: grid;
  margin-top: 22px;
  padding: 20px;
  border-radius: 18px;
  color: #ffffff;
  background: #111111;
}

.pv-contact-card small {
  color: var(--lime);
  font-family: "Inter", sans-serif;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: 0.08em;
}

.pv-contact-card strong {
  margin-top: 20px;
  font-size: 19px;
}

.pv-contact-card p {
  margin: 5px 0 14px;
  color: #9b9b96;
  font-size: 11px;
}

.pv-contact-card a {
  margin-top: 6px;
  overflow-wrap: anywhere;
  color: #d9d9d4;
  font-size: 11px;
  text-decoration: none;
}

.pv-policy-document {
  min-width: 0;
}

.pv-document-intro {
  padding: 29px;
  border: 1px solid var(--line);
  background: var(--paper);
}

.pv-document-intro p {
  margin: 0;
  color: #555552;
  font-size: 16px;
  line-height: 1.75;
  word-break: keep-all;
}

.pv-policy-section {
  scroll-margin-top: 112px;
  padding: 68px 0;
  border-bottom: 1px solid var(--line);
}

.pv-policy-section > header {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}

.pv-policy-section > header > span {
  display: grid;
  width: 41px;
  height: 41px;
  border-radius: 50%;
  color: #111111;
  background: var(--lime);
  font-family: "Inter", sans-serif;
  font-size: 10px;
  font-weight: 900;
  place-items: center;
}

.pv-policy-section > header small {
  color: var(--lime-dark);
  font-family: "Inter", sans-serif;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: 0.09em;
}

.pv-policy-section > header h2 {
  margin: 8px 0 0;
  font-size: clamp(28px, 3vw, 42px);
  line-height: 1.15;
  letter-spacing: -0.045em;
}

.pv-policy-section > p {
  margin: 24px 0 0;
  color: var(--muted);
  font-size: 15px;
  line-height: 1.72;
  word-break: keep-all;
}

/* PURPOSE */
.pv-purpose-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 27px;
  border-top: 1px solid var(--line);
  border-left: 1px solid var(--line);
}

.pv-purpose-grid article {
  min-height: 170px;
  padding: 22px;
  border-right: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}

.pv-purpose-grid h3 {
  margin: 0;
  font-size: 16px;
}

.pv-purpose-grid p {
  margin: 11px 0 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.6;
  word-break: keep-all;
}

/* TABLE */
.pv-table-wrap {
  margin-top: 27px;
  overflow-x: auto;
  border: 1px solid var(--line);
}

.pv-table-wrap table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
}

.pv-table-wrap th,
.pv-table-wrap td {
  padding: 18px;
  border-right: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  text-align: left;
  vertical-align: top;
}

.pv-table-wrap thead th {
  color: #4d4d4a;
  background: var(--paper);
  font-size: 11px;
}

.pv-table-wrap tbody th {
  width: 180px;
  font-size: 13px;
}

.pv-table-wrap td {
  color: var(--muted);
  font-size: 13px;
  line-height: 1.6;
}

/* RETENTION */
.pv-retention-list {
  display: grid;
  margin-top: 27px;
  border-top: 1px solid var(--line);
}

.pv-retention-list article {
  display: grid;
  grid-template-columns:
    minmax(0, 0.42fr)
    minmax(170px, 0.18fr)
    minmax(0, 0.40fr);
  gap: 25px;
  align-items: center;
  padding: 24px 0;
  border-bottom: 1px solid var(--line);
}

.pv-retention-list small {
  color: var(--lime-dark);
  font-family: "Inter", sans-serif;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: 0.08em;
}

.pv-retention-list h3 {
  margin: 7px 0 0;
  font-size: 17px;
}

.pv-retention-list strong {
  font-family: "Inter", sans-serif;
  font-size: 16px;
}

.pv-retention-list p {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.55;
}

.pv-notice {
  display: grid;
  grid-template-columns: 26px minmax(0, 1fr);
  gap: 11px;
  margin-top: 20px;
  padding: 17px;
  border: 1px solid #cdd9a5;
  background: #f3ffd8;
}

.pv-notice p {
  margin: 0;
  color: #515c2e;
  font-size: 12px;
  line-height: 1.55;
}

/* EMPTY */
.pv-empty-state {
  margin-top: 25px;
  padding: 24px;
  border: 1px dashed var(--line-dark);
  background: var(--paper);
}

.pv-empty-state span {
  color: #506700;
  font-weight: 850;
}

.pv-empty-state p {
  margin: 10px 0 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.6;
}

/* OVERSEAS */
.pv-overseas-card {
  margin-top: 25px;
  padding: 25px;
  color: #ffffff;
  background: #111111;
}

.pv-overseas-card dl {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  margin: 0;
  border-top: 1px solid #343434;
  border-left: 1px solid #343434;
}

.pv-overseas-card dl > div {
  min-height: 128px;
  padding: 18px;
  border-right: 1px solid #343434;
  border-bottom: 1px solid #343434;
}

.pv-overseas-card dt {
  color: var(--lime);
  font-size: 10px;
  font-weight: 850;
}

.pv-overseas-card dd {
  margin: 10px 0 0;
  color: #c3c3be;
  font-size: 12px;
  line-height: 1.6;
}

/* DESTRUCTION */
.pv-destruction-grid,
.pv-security-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 27px;
  border-top: 1px solid var(--line);
  border-left: 1px solid var(--line);
}

.pv-destruction-grid article,
.pv-security-grid article {
  min-height: 230px;
  padding: 23px;
  border-right: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}

.pv-destruction-grid article > span {
  color: var(--lime-dark);
  font-family: "Inter", sans-serif;
  font-size: 10px;
  font-weight: 900;
}

.pv-destruction-grid h3,
.pv-security-grid h3 {
  margin: 51px 0 0;
  font-size: 18px;
}

.pv-destruction-grid p,
.pv-security-grid p {
  margin: 11px 0 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.62;
  word-break: keep-all;
}

/* RIGHTS */
.pv-rights-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin: 26px 0 0;
  padding: 0;
  list-style: none;
}

.pv-rights-list li {
  position: relative;
  padding: 15px 16px 15px 34px;
  border: 1px solid var(--line);
  color: #4f4f4c;
  background: var(--paper);
  font-size: 13px;
}

.pv-rights-list li::before {
  position: absolute;
  top: 21px;
  left: 16px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--lime);
  content: "";
}

.pv-action-link {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin-top: 22px;
  padding-bottom: 7px;
  border-bottom: 1px solid #111111;
  color: #111111;
  font-size: 13px;
  font-weight: 850;
  text-decoration: none;
}

/* SECURITY */
.pv-security-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.pv-security-grid article {
  min-height: 210px;
}

.pv-security-icon {
  display: grid;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--lime);
  place-items: center;
}

.pv-security-grid h3 {
  margin-top: 30px;
}

/* OFFICER */
.pv-officer-card {
  display: grid;
  grid-template-columns: 92px minmax(0, 0.55fr) minmax(290px, 0.45fr);
  gap: 26px;
  align-items: center;
  margin-top: 26px;
  padding: 26px;
  border: 1px solid var(--line);
  background: var(--paper);
}

.pv-officer-symbol {
  display: flex;
  gap: 7px;
  align-items: center;
  justify-content: center;
}

.pv-officer-symbol i {
  display: block;
  width: 19px;
  height: 57px;
  background: var(--lime);
  transform: skewY(-38deg);
}

.pv-officer-symbol i:last-child {
  transform: translateY(-13px) skewY(-38deg);
}

.pv-officer-card small {
  color: var(--lime-dark);
  font-size: 10px;
  font-weight: 850;
}

.pv-officer-card h3 {
  margin: 8px 0 0;
  font-size: 23px;
}

.pv-officer-card p {
  margin: 6px 0 0;
  color: var(--muted);
  font-size: 12px;
}

.pv-officer-card dl {
  display: grid;
  gap: 13px;
  margin: 0;
}

.pv-officer-card dl > div {
  display: grid;
  grid-template-columns: 60px minmax(0, 1fr);
  gap: 10px;
}

.pv-officer-card dt {
  color: #858581;
  font-size: 10px;
}

.pv-officer-card dd {
  margin: 0;
  font-size: 12px;
}

.pv-officer-card a {
  color: #111111;
  text-decoration: none;
}

/* VERSION */
.pv-version-row {
  display: grid;
  grid-template-columns: 100px 190px minmax(0, 1fr);
  gap: 20px;
  align-items: center;
  margin-top: 25px;
  padding: 20px;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}

.pv-version-row span {
  color: var(--lime-dark);
  font-family: "Inter", sans-serif;
  font-size: 11px;
  font-weight: 900;
}

.pv-version-row strong {
  font-size: 13px;
}

.pv-version-row p {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
}

/* RESPONSIVE */
@media (max-width: 1100px) {
  .pv-hero-grid,
  .pv-content-grid {
    grid-template-columns: 1fr;
  }

  .pv-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pv-toc {
    position: static;
  }

  .pv-toc nav {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    border-left: 1px solid var(--line);
  }

  .pv-toc nav button {
    border-right: 1px solid var(--line);
  }

  .pv-contact-card {
    max-width: 420px;
  }
}

@media (max-width: 720px) {
  .pv-container {
    width: calc(100% - 36px);
  }

  .pv-hero {
    padding: 76px 0;
  }

  .pv-hero h1 {
    font-size: 42px;
  }

  .pv-summary-grid,
  .pv-purpose-grid,
  .pv-destruction-grid,
  .pv-security-grid,
  .pv-rights-list,
  .pv-overseas-card dl {
    grid-template-columns: 1fr;
  }

  .pv-summary-grid article,
  .pv-purpose-grid article,
  .pv-destruction-grid article,
  .pv-security-grid article {
    min-height: 0;
  }

  .pv-content {
    padding: 76px 0;
  }

  .pv-toc nav {
    grid-template-columns: 1fr 1fr;
  }

  .pv-policy-section {
    padding: 53px 0;
  }

  .pv-retention-list article {
    grid-template-columns: 1fr;
    gap: 13px;
  }

  .pv-officer-card {
    grid-template-columns: 1fr;
  }

  .pv-officer-symbol {
    justify-content: flex-start;
  }

  .pv-version-row {
    grid-template-columns: 1fr;
    gap: 10px;
  }
}
</style>
